import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { toast } from 'react-toastify';
import api from 'services/api';
import { encryptData, decryptData } from 'utils/crypto';
import { IDivida } from 'interfaces/Divida';
import { IAcordo } from 'interfaces/Acordo';
import { ILoginPayload } from 'interfaces/GeneralInterfaces';
import { IAuthContext } from 'interfaces/GeneralInterfaces';
import { IUser, ICredores } from 'interfaces/User';
import { buscaCredoresService } from 'services/buscaCredores/buscaCredores';
import secureLocalStorage from 'react-secure-storage';
import ModalSessaoEncerrada from 'components/Modal/ModalSessaoEncerrada';
import { createSignalRConnection, startConnection, stopConnection, SessionTerminatedData } from 'services/signalR';

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [bank, setBank] = useState<ICredores | null>(null);
  const [loading, setLoading] = useState(true);
  const [dividasAuthContext, setDividasAuthContext] = useState<
    IDivida[] | undefined
  >([]);
  const [acordosAuthContext, setAcordosAuthContext] = useState<
    IAcordo[] | undefined
  >([]);
  const [showSessionTerminatedModal, setShowSessionTerminatedModal] = useState(false);

  useEffect(() => {
    function setInterceptors() {
      api.interceptors.response.use(
        function (response) {
          return response;
        },
        function (error) {
          if (error?.response?.status === 401) {
            const signed = !!secureLocalStorage.getItem('@token');

            const isSessionTerminated =
              error?.response?.data?.codigo === 'SESSION_TERMINATED' ||
              error?.response?.headers?.['x-session-terminated'] === 'true';

            if (isSessionTerminated && signed) {
              setShowSessionTerminatedModal(true);
              signOut();
              return Promise.reject(error);
            }

            if (signed) {
              toast.error('Sua sessão expirou, logue novamente.');
              signOut();
            } else {
              if (error.response.config.url.includes('login')) {
                toast.error('Credenciais inválidas');
              }
            }
          }

          if (error?.response?.status !== 401 && error?.response?.status !==412) {
            const mensagem = error?.response?.data?.mensagemInsucesso;

            toast.error(
              mensagem ||
              'Desculpe, tivemos um erro! Mas não se preocupe, você pode acessar um dos canais de atendimento que já iremos te atender.',
            );
          }

          return Promise.reject(error);
        },
      );
    }

    function loadStoragedData(): void {
      const storagedUser = localStorage.getItem('@user');
      const storagedToken = secureLocalStorage.getItem('@token');
      const storagedBank = localStorage.getItem('@bank');

      if (!storagedUser || !storagedToken || !storagedBank) {
        signOut();
        setLoading(false);
        return;
      }

      if (storagedUser && storagedToken && storagedBank) {
        api.defaults.headers.Authorization = `Bearer ${storagedToken}`;
        setUser(decryptData(storagedUser) as IUser | null);
        setBank(decryptData(storagedBank) as ICredores | null);
      }

      window.my_global_var = decryptData(storagedUser)?.cnpJ_CPF || '';

      setLoading(false);
    }

    setInterceptors();
    loadStoragedData();
  }, []);

  // Conectar ao SignalR quando usuário estiver autenticado
  useEffect(() => {
    if (!user || !bank) return;

    const handleSessionTerminated = (_data: SessionTerminatedData) => {
      setShowSessionTerminatedModal(true);
      signOut();
    };

    createSignalRConnection(handleSessionTerminated);
    startConnection().catch(console.error);

    return () => {
      stopConnection();
    };
  }, [user, bank]);

  const signIn = async (): Promise<IUser | undefined> => {

    // signOut();

    api.defaults.headers.Authorization = `Bearer ${secureLocalStorage.getItem("@token")}`;

    const { data: loggedUser } = await buscaCredoresService(
      {
        headers: {
          Authorization: `Bearer ${secureLocalStorage.getItem("@token")}`
        }
      }
    );


    if (!loggedUser?.mensagemRetorno?.sucesso) {
      const emptyUser: IUser = {
        cnpJ_CPF: undefined,
        credores: undefined,
        nome: undefined,
        mensagemRetorno: loggedUser.mensagemRetorno
      }
      return emptyUser;
    }

    if (!loggedUser?.credores?.length) return;

    setUser(loggedUser);
    localStorage.setItem('@user', encryptData(loggedUser));

    window.my_global_var = loggedUser?.cnpJ_CPF || '';

    return loggedUser;
  };

  async function signOut() {
    await stopConnection();
    localStorage.clear();
    setUser(null);
    setBank(null);
    setDividasAuthContext([]);
    setAcordosAuthContext([]);
  }

  const handleSessionTerminatedClose = () => {
    setShowSessionTerminatedModal(false);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        signed: !!user && !!bank,
        user,
        bank,
        setBank,
        signIn,
        signOut,
        loading,
        dividasAuthContext,
        acordosAuthContext,
        setDividasAuthContext,
        setAcordosAuthContext,
      }}
    >
      {children}
      <ModalSessaoEncerrada
        open={showSessionTerminatedModal}
        onClose={handleSessionTerminatedClose}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;

export function useAuth(): IAuthContext {
  const context = useContext(AuthContext);

  return context;
}
